import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Heart, ArrowRight, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date_of_birth: '',
    gender: '',
    location: { city: '', country: '' },
    bio: '',
    relationship_goals: '',
    core_values: [],
    lifestyle_habits: { exercise: '', diet: '', smoking: '', drinking: '' },
    personality_type: '',
    faith_spiritual: '',
    love_languages: [],
    dealbreakers: [],
    commitment_readiness: 5,
    emotional_availability: 5
  });

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${sessionToken}` };
      await axios.put(`${API}/profile`, formData, { headers });
      await axios.post(`${API}/auth/complete-onboarding`, {}, { headers });
      // refresh the app-wide user so nav/gates reflect the completed profile
      try {
        const me = await axios.get(`${API}/auth/me`, { headers });
        setUser && setUser(me.data);
      } catch (e) { /* non-blocking */ }
      toast.success('Profile completed! Now pick the plan that fits you.');
      navigate('/pricing?welcome=1');
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const valueOptions = ['Honesty', 'Family', 'Faith', 'Ambition', 'Kindness', 'Loyalty', 'Adventure', 'Stability', 'Humor', 'Independence'];
  const loveLanguageOptions = ['Words of Affirmation', 'Quality Time', 'Physical Touch', 'Acts of Service', 'Receiving Gifts'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Heart className="w-12 h-12 text-rose-500 fill-rose-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-rose-900 mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Complete Your Profile
          </h1>
          <p className="text-gray-600">Help us find your perfect match</p>
          <Progress value={progress} className="mt-4 h-2" />
        </div>

        <Card className="shadow-xl border-rose-100">
          <CardHeader>
            <CardTitle>Step {step} of {totalSteps}</CardTitle>
            <CardDescription>
              {step === 1 && 'Basic Information'}
              {step === 2 && 'Your Values & Goals'}
              {step === 3 && 'Lifestyle & Personality'}
              {step === 4 && 'Love Languages & Dealbreakers'}
              {step === 5 && 'Commitment & Availability'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {step === 1 && (
              <>
                <div>
                  <Label htmlFor="dob">Date of Birth</Label>
                  <Input
                    id="dob"
                    type="date"
                    data-testid="dob-input"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                    <SelectTrigger data-testid="gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      data-testid="city-input"
                      value={formData.location.city}
                      onChange={(e) => setFormData({ ...formData, location: { ...formData.location, city: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      data-testid="country-input"
                      value={formData.location.country}
                      onChange={(e) => setFormData({ ...formData, location: { ...formData.location, country: e.target.value } })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    data-testid="bio-textarea"
                    placeholder="Tell us about yourself..."
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={4}
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <Label htmlFor="goals">Relationship Goals</Label>
                  <Select value={formData.relationship_goals} onValueChange={(v) => setFormData({ ...formData, relationship_goals: v })}>
                    <SelectTrigger data-testid="goals-select">
                      <SelectValue placeholder="What are you looking for?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="marriage">Marriage</SelectItem>
                      <SelectItem value="long-term">Long-term Relationship</SelectItem>
                      <SelectItem value="life-partner">Life Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Core Values (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {valueOptions.map((value) => (
                      <div key={value} className="flex items-center space-x-2">
                        <Checkbox
                          id={value}
                          data-testid={`value-${value.toLowerCase()}`}
                          checked={formData.core_values.includes(value)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, core_values: [...formData.core_values, value] });
                            } else {
                              setFormData({ ...formData, core_values: formData.core_values.filter(v => v !== value) });
                            }
                          }}
                        />
                        <Label htmlFor={value} className="font-normal cursor-pointer">{value}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="faith">Faith/Spiritual Beliefs</Label>
                  <Input
                    id="faith"
                    data-testid="faith-input"
                    placeholder="e.g., Christian, Muslim, Spiritual, etc."
                    value={formData.faith_spiritual}
                    onChange={(e) => setFormData({ ...formData, faith_spiritual: e.target.value })}
                  />
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div>
                  <Label htmlFor="personality">Personality Type</Label>
                  <Input
                    id="personality"
                    data-testid="personality-input"
                    placeholder="e.g., INTJ, Extroverted, etc."
                    value={formData.personality_type}
                    onChange={(e) => setFormData({ ...formData, personality_type: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="exercise">Exercise</Label>
                    <Select
                      value={formData.lifestyle_habits.exercise}
                      onValueChange={(v) => setFormData({ ...formData, lifestyle_habits: { ...formData.lifestyle_habits, exercise: v } })}
                    >
                      <SelectTrigger data-testid="exercise-select">
                        <SelectValue placeholder="Frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="rarely">Rarely</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="diet">Diet</Label>
                    <Select
                      value={formData.lifestyle_habits.diet}
                      onValueChange={(v) => setFormData({ ...formData, lifestyle_habits: { ...formData.lifestyle_habits, diet: v } })}
                    >
                      <SelectTrigger data-testid="diet-select">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vegan">Vegan</SelectItem>
                        <SelectItem value="vegetarian">Vegetarian</SelectItem>
                        <SelectItem value="pescatarian">Pescatarian</SelectItem>
                        <SelectItem value="omnivore">Omnivore</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="smoking">Smoking</Label>
                    <Select
                      value={formData.lifestyle_habits.smoking}
                      onValueChange={(v) => setFormData({ ...formData, lifestyle_habits: { ...formData.lifestyle_habits, smoking: v } })}
                    >
                      <SelectTrigger data-testid="smoking-select">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="socially">Socially</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="drinking">Drinking</Label>
                    <Select
                      value={formData.lifestyle_habits.drinking}
                      onValueChange={(v) => setFormData({ ...formData, lifestyle_habits: { ...formData.lifestyle_habits, drinking: v } })}
                    >
                      <SelectTrigger data-testid="drinking-select">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="socially">Socially</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <div>
                  <Label>Love Languages (Select all that apply)</Label>
                  <div className="space-y-3 mt-2">
                    {loveLanguageOptions.map((lang) => (
                      <div key={lang} className="flex items-center space-x-2">
                        <Checkbox
                          id={lang}
                          data-testid={`love-lang-${lang.toLowerCase().replace(/ /g, '-')}`}
                          checked={formData.love_languages.includes(lang)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, love_languages: [...formData.love_languages, lang] });
                            } else {
                              setFormData({ ...formData, love_languages: formData.love_languages.filter(l => l !== lang) });
                            }
                          }}
                        />
                        <Label htmlFor={lang} className="font-normal cursor-pointer">{lang}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="dealbreakers">Dealbreakers (one per line)</Label>
                  <Textarea
                    id="dealbreakers"
                    data-testid="dealbreakers-textarea"
                    placeholder="e.g., Smoking\nDishonesty\nNo family values"
                    rows={4}
                    value={formData.dealbreakers.join('\n')}
                    onChange={(e) => setFormData({ ...formData, dealbreakers: e.target.value.split('\n').filter(d => d.trim()) })}
                  />
                </div>
              </>
            )}

            {step === 5 && (
              <>
                <div>
                  <Label htmlFor="commitment">Commitment Readiness (1-10)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <Input
                      id="commitment"
                      data-testid="commitment-input"
                      type="range"
                      min="1"
                      max="10"
                      value={formData.commitment_readiness}
                      onChange={(e) => setFormData({ ...formData, commitment_readiness: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-2xl font-bold text-rose-600 w-12 text-center">{formData.commitment_readiness}</span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="emotional">Emotional Availability (1-10)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <Input
                      id="emotional"
                      data-testid="emotional-input"
                      type="range"
                      min="1"
                      max="10"
                      value={formData.emotional_availability}
                      onChange={(e) => setFormData({ ...formData, emotional_availability: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-2xl font-bold text-rose-600 w-12 text-center">{formData.emotional_availability}</span>
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-between pt-6">
              <Button
                data-testid="prev-step-btn"
                variant="outline"
                onClick={() => setStep(step - 1)}
                disabled={step === 1}
                className="border-rose-200"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              {step < totalSteps ? (
                <Button
                  data-testid="next-step-btn"
                  onClick={() => setStep(step + 1)}
                  className="bg-rose-500 hover:bg-rose-600"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  data-testid="complete-profile-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-rose-500 hover:bg-rose-600"
                >
                  {loading ? 'Saving...' : 'Complete Profile'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;